import {useFormik} from 'formik';
import {useAuth} from '../../../entities';
import {getSessionItem, setSessionItem, removeSessionItem, useSchemes} from '../../../shared';
import {useState, useCallback, useEffect} from 'react';
import {useNavigate} from 'react-router';
import {useQueryClient} from '@tanstack/react-query';

export const useCodeForm = () => {
  const {codeSchema} = useSchemes();
  const {loginWithCode, errorMessage, requestCodeLogin, initLoginFlow} = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [flowId, setFlowId] = useState<string | null>(getSessionItem('auth_flow_id'));
  const [csrfToken, setCsrfToken] = useState<string | null>(getSessionItem('auth_csrf_token'));
  const identifier = getSessionItem('auth_identifier');

  // Получаем время окончания таймера из sessionStorage или устанавливаем новое
  const getInitialTimer = () => {
    // Проверяем, истек ли таймер ранее
    if (getSessionItem('auth_timer_expired') === 'true') {
      return 0;
    }

    const endTime = getSessionItem('auth_timer_end');
    // Проверяем, существует ли время окончания таймера
    if (endTime) {
      const remaining = Math.ceil((parseInt(endTime) - Date.now()) / 1000);
      // Если времени осталось меньше или равно 0, возвращаем 0 и помечаем таймер как истекший
      if (remaining <= 0) {
        removeSessionItem('auth_timer_end');
        setSessionItem('auth_timer_expired', 'true');
        return 0;
      }
      return remaining;
    }
    return 60;
  };

  const [timer, setTimer] = useState<number>(getInitialTimer());
  const [isTimerActive, setIsTimerActive] = useState<boolean>(timer > 0);

  // Запускаем таймер при монтировании компонента
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    if (isTimerActive && timer > 0) {
      // Сохраняем время окончания таймера в sessionStorage при его запуске
      if (!getSessionItem('auth_timer_end')) {
        setSessionItem('auth_timer_end', (Date.now() + timer * 1000).toString());
      }

      interval = setInterval(() => {
        setTimer((prevTimer) => {
          const newValue = prevTimer - 1;
          if (newValue <= 0) {
            // Если таймер закончился, удаляем значение из sessionStorage и помечаем как истекший
            removeSessionItem('auth_timer_end');
            setSessionItem('auth_timer_expired', 'true');
            setIsTimerActive(false);
            return 0;
          }
          return newValue;
        });
      }, 1000);
    } else if (timer === 0) {
      setIsTimerActive(false);
      // Дополнительно убеждаемся, что значение удалено из sessionStorage и таймер помечен как истекший
      removeSessionItem('auth_timer_end');
      setSessionItem('auth_timer_expired', 'true');
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [timer, isTimerActive]);

  // Функция для перезапуска таймера
  const resetTimer = useCallback(() => {
    setTimer(60);
    setIsTimerActive(true);
    // Обновляем время окончания в sessionStorage и сбрасываем флаг истекшего таймера
    setSessionItem('auth_timer_end', (Date.now() + 60 * 1000).toString());
    removeSessionItem('auth_timer_expired');
  }, []);

  // Функция для повторной отправки кода
  const handleResendCode = useCallback(async () => {
    if (!identifier) return;

    try {
      // Отправляем запрос на получение нового потока
      await initLoginFlow();

      // Получаем обновленные значения из sessionStorage
      const newFlowId = getSessionItem('auth_flow_id');
      const newCsrfToken = getSessionItem('auth_csrf_token');

      if (!newFlowId || !newCsrfToken) {
        console.error('Failed to get new flow ID or CSRF token');
        return;
      }

      // Обновляем локальное состояние
      setFlowId(newFlowId);
      setCsrfToken(newCsrfToken);

      // Используем новые значения для запроса кода
      await requestCodeLogin({
        identifier,
        flowId: newFlowId,
        csrfToken: newCsrfToken,
      });

      resetTimer();
    } catch (error) {
      resetTimer();
      console.error('Failed to resend code:', error);
    }
  }, [identifier, initLoginFlow, requestCodeLogin, resetTimer]);

  const onSubmit = async ({code}: {code: string}) => {
    if (!flowId || !identifier || !csrfToken) {
      return;
    }

    try {
      // 1. Выполняем логин
      await loginWithCode({
        identifier,
        code,
        flowId,
        csrfToken,
      });
      // При успешном логине удаляем данные таймера
      removeSessionItem('auth_timer_end');
      removeSessionItem('auth_timer_expired');
      // 2. Принудительно перезапрашиваем данные сессии и ждем завершения
      await queryClient.refetchQueries({queryKey: ['session'], exact: true});
      // 3. Только теперь, когда сессия обновлена, делаем навигацию
      navigate('/', {replace: true});
    } catch (error) {
      // Обрабатываем ошибку логина или рефетча сессии
      console.error('Login or session refetch failed:', error);
    }
  };

  const formik = useFormik({
    initialValues: {
      code: '',
    },
    validationSchema: codeSchema,
    onSubmit,
  });

  return {
    ...formik,
    timer,
    isTimerActive,
    handleResendCode,
    errorMessage,
  };
};
